import React from "react";
import { TextField } from "@mui/material";
import type { Job } from "../../../../../../api/types";
import { ImportTableColumn } from "../../../../../../utilities/types";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { useValidationErrors } from "../../../ValidationErrorsContext";
import { useDateColumnViewModel } from "./dateColumn.vm";

export interface DateColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** The field name on the Job object */
	fieldName: keyof Job;
	/** The column identifier */
	columnId: ImportTableColumn;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly date picker component for ImportTable fields.
 * Simplified version of dateColumn that doesn't depend on MRT.
 * Uses native date input for best mobile experience.
 */
export const DateColumnMobile: React.FC<DateColumnMobileProps> = ({
	job,
	columnContext,
	fieldName,
	columnId,
	required = false,
	disabled = false,
	label,
}) => {
	const vm = useDateColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const errorKey = getValidationErrorKey(columnId, job.id);

	// Get formatted date value using viewmodel
	const currentValue = vm.getCurrentDateValue(job, fieldName);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		vm.handleDateChange(job.id, fieldName, event.target.value);
	};

	const handleBlur = () => {
		if (disabled) return;
		// Get the updated job from context (it was modified in handleChange)
		const updatedJob = columnContext.jobs.find((j) => j.id === job.id);
		if (updatedJob) {
			vm.handleDateBlur(updatedJob, fieldName);
		}
	};

	return (
		<TextField
			size="small"
			type="date"
			variant="outlined"
			required={required}
			disabled={disabled}
			fullWidth
			label={label || columnDisplayNames[columnId]}
			value={currentValue}
			error={!!validationErrors?.[errorKey]}
			helperText={validationErrors?.[errorKey]}
			onChange={handleChange}
			onBlur={handleBlur}
			InputLabelProps={{
				shrink: true, // Keep label above for date inputs
			}}
		/>
	);
};
